/**
 * PremiumScreen — pannello unico per attivazione Premium + gestione sigil
 * derivati.
 *
 * Non-premium: mostra cosa si sblocca, niente azioni se non "richiedi
 * attivazione" (placeholder per quando Stripe sara' integrato).
 *
 * Premium: lista sigil derivati con creazione/tombamento + (futuro) toggle
 * multi-device per chat dal pannello chat stesso.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box, FieldLabel, Cursor } from '../components/Frame';
import { Section } from '../components/Section';
import { useStore } from '../lib/store';
import {
  getMyAccount, createDerivedSigil, listMyDerivedSigils, tombDerivedSigil,
  localKeysForDerivedSigil, setIdentityMode, setSigilFindable,
} from '../lib/derivedSigil';
import { callFn } from '../lib/firebase';
import { rotateMnemonic as doRotateMnemonic } from '../lib/rotateMnemonic';

export default function PremiumScreen() {
  const navigate = useNavigate();
  const { sigil, mnemonic, addDerivedKeys, removeDerivedKeys, setPremium: setPremiumStore, setIdentity, setBoxKeys } = useStore();
  const [rotPin, setRotPin] = useState('');
  const [rotBusy, setRotBusy] = useState(false);
  const [rotNew, setRotNew] = useState(null);
  const [rotErr, setRotErr] = useState(null);
  const [grantTo, setGrantTo] = useState('');
  const [grantDays, setGrantDays] = useState('');
  const [grantBusy, setGrantBusy] = useState(false);
  const [grantMsg, setGrantMsg] = useState(null);
  const [premiumList, setPremiumList] = useState(null); // null = mai caricato
  const [premiumListBusy, setPremiumListBusy] = useState(false);
  const [premiumListErr, setPremiumListErr] = useState(null);

  async function loadPremiumList() {
    setPremiumListErr(null);
    setPremiumListBusy(true);
    try {
      const res = await callFn('listPremiumUsers')({});
      setPremiumList(res?.data?.users || []);
    } catch (e) {
      const m = String(e?.message || '');
      setPremiumListErr(m.includes('FOUNDER_ONLY') ? 'solo il founder può vedere l\'elenco' : `errore: ${m}`);
    } finally { setPremiumListBusy(false); }
  }

  async function doGrantTo(premiumOn) {
    setGrantMsg(null);
    const s = grantTo.trim().replace(/^@/, '');
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(s)) { setGrantMsg('sigil non valido'); return; }
    const d = parseInt(grantDays, 10);
    const days = Number.isFinite(d) && d > 0 ? d : 0; // 0 = a vita
    setGrantBusy(true);
    try {
      const res = await callFn('grantPremium')({ sigil: s, premium: premiumOn, days });
      if (res?.data?.ok) {
        const exp = res.data.expiresAt;
        const when = exp ? `scade il ${new Date(exp).toLocaleDateString('it-IT')}` : 'a vita';
        setGrantMsg(`@${s}: Premium ${premiumOn ? `ATTIVATO ✓ (${when})` : 'rimosso'}`);
        setGrantTo(''); setGrantDays('');
        if (premiumList !== null) loadPremiumList(); // tieni l'elenco aggiornato
      } else {
        setGrantMsg('risposta inattesa');
      }
    } catch (e) {
      const m = String(e?.message || '');
      if (m.includes('FOUNDER_ONLY')) setGrantMsg('solo il founder può attivarlo');
      else if (m.includes('SIGIL_NOT_FOUND')) setGrantMsg('sigil non trovato (deve essersi già registrato)');
      else setGrantMsg(`errore: ${m}`);
    } finally { setGrantBusy(false); }
  }

  const [loading, setLoading] = useState(true);
  const [premium, setPremium] = useState(false);
  const [premStatus, setPremStatus] = useState('none');
  const [premExpiresAt, setPremExpiresAt] = useState(null);
  const [premDaysLeft, setPremDaysLeft] = useState(null);
  const [premGrace, setPremGrace] = useState(null);
  const [idMode, setIdMode] = useState('open');
  const [modeBusy, setModeBusy] = useState(false);
  const [modeMsg, setModeMsg] = useState(null);
  const [derived, setDerived] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newSigil, setNewSigil] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [showMnemonic, setShowMnemonic] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getMyAccount();
        if (!cancelled) {
          setPremium(!!r.premium);
          setPremStatus(r.premiumStatus || 'none');
          setPremExpiresAt(r.premiumExpiresAt ?? null);
          setPremDaysLeft(r.premiumDaysLeft ?? null);
          setPremGrace(r.premiumGraceDaysLeft ?? null);
          setIdMode(r.identityMode || 'open');
          setDerived(r.derivedSigils || []);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.message || 'errore');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function refresh() {
    try {
      const r = await getMyAccount();   // include findable per ogni sigil
      setDerived(r.derivedSigils || []);
    } catch {}
  }

  async function toggleFindable(s, current) {
    try {
      await setSigilFindable(s, !current);
      await refresh();
    } catch (e) {
      setErr(e?.message || 'toggle visibilità fallito');
    }
  }

  async function changeMode(mode) {
    setModeMsg(null);
    if (mode === idMode) return;
    if (mode === 'reserved' && !confirm('Solo su invito: il tuo @sigil sparisce dalla ricerca. Ti scrive solo chi ha un tuo link di invito. Le chat esistenti restano. Confermi?')) return;
    setModeBusy(true);
    try {
      await setIdentityMode(mode);
      setIdMode(mode);
      setModeMsg(mode === 'reserved'
        ? 'ora sei "solo su invito": invisibile alla ricerca.'
        : mode === 'hybrid'
        ? 'modalità Ibrida attiva: ricercabile + sigil derivati.'
        : 'ora sei ricercabile: chi conosce il tuo sigil può scriverti.');
    } catch (e) {
      const m = String(e?.message || '');
      if (m.includes('PREMIUM_REQUIRED')) setModeMsg('la modalità Ibrida richiede Premium.');
      else setModeMsg(`errore: ${m}`);
    } finally {
      setModeBusy(false);
    }
  }

  async function doCreate() {
    setErr(null);
    const sigilName = newSigil.trim();
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(sigilName)) {
      setErr('sigil: 3-20 caratteri, solo lettere/numeri/underscore');
      return;
    }
    if (!mnemonic) {
      setErr('24 parole non in memoria. esci, entra col PIN e riprova.');
      return;
    }
    setBusy(true);
    try {
      await createDerivedSigil({ mnemonic, sigil: sigilName, label: newLabel.trim() });
      // Aggiungi subito le chiavi derivate allo store globale (per
      // decifratura e dropdown "Emetti come" in InviteCreateScreen).
      try {
        const keys = await localKeysForDerivedSigil({ mnemonic, sigilLower: sigilName.toLowerCase() });
        addDerivedKeys?.(sigilName.toLowerCase(), { ...keys, sigil: sigilName });
      } catch (e2) { console.warn('local key derive failed', e2); }
      setNewSigil(''); setNewLabel('');
      await refresh();
    } catch (e) {
      const m = String(e?.message || '');
      if (m.includes('SIGIL_TAKEN')) setErr('quel nome è già preso');
      else if (m.includes('PREMIUM_EXPIRED')) setErr('Premium in grazia: ora puoi solo leggere. Rinnova per creare nuovi sigil.');
      else if (m.includes('PREMIUM_REQUIRED')) setErr('attiva Premium per creare sigil derivati.');
      else if (m.includes('MAX_DERIVED_REACHED')) setErr('sei al massimo (20). Tomba prima un sigil esistente.');
      else if (m.includes('SIGIL_RESERVED')) setErr('quel nome è riservato')
      else setErr(m || 'errore creazione');
    } finally {
      setBusy(false);
    }
  }

  async function doRotate() {
    setRotErr(null);
    if (!/^[0-9]{6}$/.test(rotPin)) { setRotErr('inserisci il PIN attuale (6 cifre)'); return; }
    if (!confirm(
      'CAMBIARE LE 24 PAROLE?\n\n'
      + 'Nuove parole per lo STESSO @' + (sigil || '') + '.\n\n'
      + '• I tuoi contatti vedranno "chiave cambiata" e dovranno ri-verificare il codice di sicurezza.\n'
      + '• I messaggi vecchi non ancora letti potrebbero diventare illeggibili.\n'
      + '• Gli altri tuoi device vanno ri-configurati con le nuove parole.\n\n'
      + 'Fallo solo se temi che le vecchie parole siano finite in mani altrui.'
    )) return;
    setRotBusy(true);
    try {
      const r = await doRotateMnemonic({
        sigil, pin: rotPin,
        applyToStore: ({ mnemonic: m, publicKey, secretKey, uid, boxPub, boxSec }) => {
          setIdentity({ publicKey, secretKey, uid, mnemonic: m });
          setBoxKeys?.({ boxPub, boxSec });
        },
      });
      setRotNew(r.mnemonic);
      setRotPin('');
    } catch (e) {
      const m = String(e?.message || '');
      if (m.includes('NOT_OWNER')) setRotErr('non risulti proprietario del sigil');
      else setRotErr(m || 'rotazione fallita');
    } finally {
      setRotBusy(false);
    }
  }

  async function doTomb(s) {
    if (!confirm(`Tombare @${s}? Nessuno potrà più scrivergli. Le chat passate restano nei tuoi messaggi, ma il sigil sparisce dalla rete.`)) return;
    setBusy(true);
    try {
      await tombDerivedSigil(s);
      removeDerivedKeys?.((s || '').toLowerCase());
      await refresh();
    } catch (e) {
      setErr(e?.message || 'tombamento fallito');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Frame statusLeft="PREMIUM" statusRight="5G">
      <Header title="// Premium" crumb="◀ indietro" onCrumbClick={() => navigate('/inbox')} />

      {loading ? (
        <Box className="text-[15px] text-ink-dim"><Cursor /> carico il tuo account...</Box>
      ) : (
        // Selettore modalità identità — disponibile a tutti (Ibrida=premium)
        <Section icon="🎭" title="Chi può trovarti" subtitle="la tua visibilità">
          <p className="text-[13px] text-ink-dim">
            Scegli come le persone ti raggiungono. Tocca un'opzione.
          </p>
          <div className="space-y-2">
            {[
              { v: 'open', label: '🔎 Ricercabile', desc: 'Chi conosce il tuo @sigil esatto può scriverti.' },
              { v: 'reserved', label: '🔒 Solo su invito', desc: 'Non compari nella ricerca. Ti scrive solo chi ha un tuo link di invito.' },
              { v: 'hybrid', label: '🎭 Ibrida — Premium', desc: 'Ricercabile, più i sigil derivati: identità separate per amici, lavoro…' },
            ].map((o) => {
              const active = idMode === o.v;
              return (
                <button
                  key={o.v}
                  onClick={() => changeMode(o.v)}
                  disabled={modeBusy}
                  className={`w-full text-left border rounded-xl px-3 py-2.5 transition-colors disabled:opacity-50 ${active ? 'border-ink bg-ink/10' : 'border-ink-dim/50 hover:border-ink-dim hover:bg-ink/[0.04]'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[15px] ${active ? 'text-ink text-glow' : 'text-ink'}`}>{o.label}</span>
                    {active && <span className="text-[12px] text-ink shrink-0">✓ attiva</span>}
                  </div>
                  <div className="text-[12px] text-ink-dim mt-1 leading-snug">{o.desc}</div>
                </button>
              );
            })}
          </div>
          {modeMsg && <p className="text-[12px] text-amber break-words">{modeMsg}</p>}
        </Section>
      )}

      {loading ? null : !premium ? (
        <>
          <Box variant="warn" className="text-[14px] mb-3 space-y-2">
            <p className="text-amber">🔒 Premium non attivo</p>
            <p className="text-ink-dim">
              La tua identità @{sigil || '???'} funziona come sempre.
              Premium aggiunge funzioni extra. La sicurezza resta gratis per tutti.
            </p>
          </Box>

          <Box className="text-[14px] space-y-2 mb-3">
            <p className="text-ink">Con Premium hai:</p>
            <ul className="list-disc pl-5 space-y-1 text-ink-dim">
              <li><b>Sigil derivati</b> (fino a 20): identità separate per amici,
                lavoro, attivismo. Chi ti scrive lì non conosce il tuo sigil
                principale. Nascono dalle tue 24 parole: niente altro da ricordare.</li>
              <li><b>Chat su più device</b> (in arrivo): per ogni chat scegli se
                riceverla solo sull'ultimo device usato o su tutti insieme.</li>
            </ul>
          </Box>

          <Btn variant="primary" onClick={async () => {
            setErr(null); setBusy(true);
            try {
              const res = await callFn('createCheckoutSession')({});
              if (res?.data?.ok && res.data.url) {
                window.location.href = res.data.url;
                return;
              }
              setErr('impossibile avviare il pagamento.');
            } catch (e) {
              const m = String(e?.message || '');
              if (m.includes('STRIPE_NOT_CONFIGURED')) {
                setErr('Pagamenti non ancora attivi. Scrivi a info@nullum.app per l\'attivazione manuale.');
              } else setErr(m || 'errore checkout');
            } finally { setBusy(false); }
          }} disabled={busy}>
            💳 Attiva Premium
          </Btn>

          <Box className="text-[13px] text-ink-dim my-3">
            <p>Oppure scrivi a <span className="text-ink select-all">info@nullum.app</span>
            {' '}per attivarlo a mano (durante la beta).</p>
          </Box>

          <Btn variant="ghost" onClick={async () => {
            setErr(null); setBusy(true);
            try {
              const res = await callFn('grantPremium')({ sigil, premium: true });
              if (res?.data?.ok) {
                setPremium(true);
                setPremiumStore?.(true);
                await refresh();
              }
            } catch (e) {
              const m = String(e?.message || '');
              if (m.includes('FOUNDER_ONLY')) setErr('Funzione admin: solo il founder puo grant a se stesso.');
              else setErr(m || 'errore grant');
            } finally { setBusy(false); }
          }} disabled={busy}>
            🔑 Grant Premium a me (admin / founder)
          </Btn>

          <div className="mt-4 border-t border-ink-dim/40 pt-3">
            <Btn variant="ghost" onClick={() => setShowMnemonic((v) => !v)}>
              {showMnemonic ? '🔒 Nascondi 24 parole' : '📜 Mostra le mie 24 parole'}
            </Btn>
            {showMnemonic && (
              <Box variant="warn" className="mt-2 text-[13px] space-y-2">
                {!mnemonic ? (
                  <p className="text-amber">non in memoria. esci, entra col PIN e riprova.</p>
                ) : (
                  <>
                    <p className="text-amber">
                      ⚠ Scrivile su carta. Niente screenshot, niente note sul cloud.
                      Chi le ha può fingersi te.
                    </p>
                    <div className="border border-amber p-2 font-mono text-[14px] text-ink text-glow break-words select-all">
                      {mnemonic}
                    </div>
                  </>
                )}
              </Box>
            )}
          </div>

          <Footer>
            <Btn variant="ghost" onClick={() => navigate('/inbox')}>◀ Inbox</Btn>
          </Footer>
        </>
      ) : (
        <>
          <Box variant={premStatus === 'grace' ? 'warn' : 'success'} className="text-[14px] mb-3">
            {premStatus === 'lifetime'
              ? `✓ Premium attivo (a vita) per @${sigil || '???'}`
              : premStatus === 'active'
              ? `✓ Premium attivo per @${sigil || '???'}${premExpiresAt ? ` — scade il ${new Date(premExpiresAt).toLocaleDateString('it-IT')}` : ''}${premDaysLeft != null ? ` (tra ${premDaysLeft} gg)` : ''}`
              : premStatus === 'grace'
              ? `⚠ Premium scaduto — periodo di grazia${premGrace != null ? ` (${premGrace} gg)` : ''}: ora puoi solo leggere. Rinnova per creare di nuovo.`
              : `✓ Premium attivo per @${sigil || '???'}`}
          </Box>

          <Section icon="🪪" title="Sigil derivati" subtitle="identità separate (Premium)">
          <p className="text-[13px] text-ink-dim mb-3">
            ogni sigil derivato è un'identità separata, nata dalle tue 24 parole
            (stesso nome → stesse chiavi). Chi ti scrive lì non vede il tuo @{sigil}.
          </p>

          {derived.length === 0 ? (
            <Box className="text-[13px] text-ink-dim mb-3">
              nessun sigil derivato. creane uno qui sotto.
            </Box>
          ) : (
            <div className="space-y-2 mb-4">
              {derived.map((d) => (
                <div key={d.sigilLower} className="border border-ink-dim p-2 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-ink text-[15px] truncate">@{d.sigil}</div>
                    {d.labelLocal && <div className="text-ink-dim text-[12px]">label: {d.labelLocal}</div>}
                    <div className="text-[11px] text-ink-dim">{d.findable ? '👁 ricercabile' : '🚫 solo invito'}</div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      className="text-[12px] border border-ink-dim px-2 py-1 hover:border-ink text-ink-dim hover:text-ink"
                      onClick={() => toggleFindable(d.sigil, d.findable)}
                      disabled={busy}
                    >
                      {d.findable ? '🚫 rendi privato' : '👁 rendi cercabile'}
                    </button>
                    <button
                      className="text-red-400 hover:text-red-300 text-[12px] border border-red-500/40 px-2 py-1"
                      onClick={() => doTomb(d.sigil)}
                      disabled={busy}
                    >
                      ✕ tomba
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2 border-t border-ink-dim/40 pt-3">
            <FieldLabel>&gt; nuovo sigil derivato</FieldLabel>
            <input
              type="text" value={newSigil} onChange={(e) => setNewSigil(e.target.value)}
              placeholder="es. vento_libero" maxLength={20}
              className="w-full bg-black border border-ink-dim text-ink font-mono text-[16px] px-3 py-2 focus:border-ink focus:outline-none"
            />
            <FieldLabel>&gt; nota per te (opzionale)</FieldLabel>
            <input
              type="text" value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
              placeholder="es. amici" maxLength={40}
              className="w-full bg-black border border-ink-dim text-ink font-mono text-[16px] px-3 py-2 focus:border-ink focus:outline-none"
            />
            {err && (
              <Box variant="danger" className="text-[13px]"><span className="text-red-500">✕</span> {err}</Box>
            )}
            <Btn variant="primary" onClick={doCreate} disabled={busy || !newSigil.trim()}>
              {busy ? '...' : '✦ Crea sigil derivato'}
            </Btn>
          </div>
          </Section>

          <Section icon="🔁" title="Ruota le 24 parole" subtitle="rigenera la mnemonica" tone="danger">
            <p className="text-[12px] text-ink-dim">
              nuove 24 parole per lo stesso @{sigil}. Usalo se temi che le vecchie
              siano state viste. I contatti dovranno ri-verificare il codice di
              sicurezza; gli altri tuoi device vanno ri-configurati.
            </p>
            {!rotNew ? (
              <>
                <input
                  type="password" inputMode="numeric" value={rotPin}
                  onChange={(e) => setRotPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="PIN attuale (6 cifre)" maxLength={6}
                  className="w-full bg-black border border-ink-dim text-ink font-mono text-[16px] px-3 py-2 focus:border-ink focus:outline-none"
                />
                {rotErr && <p className="text-[13px] text-red-400">{rotErr}</p>}
                <Btn variant="danger" onClick={doRotate} disabled={rotBusy}>
                  {rotBusy ? '...' : '🔁 Ruota mnemonica'}
                </Btn>
              </>
            ) : (
              <Box variant="warn" className="text-[13px] space-y-2">
                <p className="text-amber">✓ Fatto. Nuove 24 parole — scrivile su carta adesso, le vecchie non valgono più:</p>
                <div className="border border-amber p-2 font-mono text-[14px] text-ink text-glow break-words select-all">
                  {rotNew}
                </div>
                <Btn variant="ghost" onClick={() => setRotNew(null)}>Ho salvato — chiudi</Btn>
              </Box>
            )}
          </Section>

          <Section icon="📜" title="Le mie 24 parole" subtitle="mostrale e salvale su carta">
            <Btn variant="ghost" onClick={() => setShowMnemonic((v) => !v)}>
              {showMnemonic ? '🔒 Nascondi 24 parole' : '📜 Mostra le mie 24 parole'}
            </Btn>
            {showMnemonic && (
              <Box variant="warn" className="mt-2 text-[13px] space-y-2">
                {!mnemonic ? (
                  <p className="text-amber">non in memoria. esci, entra col PIN e riprova.</p>
                ) : (
                  <>
                    <p className="text-amber">
                      ⚠ Scrivile su carta. Niente screenshot, niente note sul cloud.
                      Chi le ha può fingersi te.
                    </p>
                    <div className="border border-amber p-2 font-mono text-[14px] text-ink text-glow break-words select-all">
                      {mnemonic}
                    </div>
                  </>
                )}
              </Box>
            )}
          </Section>

          <Section icon="🛠" title="Strumenti admin" subtitle="solo founder">
            {/* Attiva/disattiva Premium a un tester via sigil */}
            <div className="space-y-2 border border-ink-dim/40 p-2">
              <p className="text-[13px] text-ink">⭐ Premium a un tester</p>
              <input
                type="text" value={grantTo} onChange={(e) => setGrantTo(e.target.value)}
                placeholder="@sigil_del_tester" maxLength={21}
                className="w-full bg-black border border-ink-dim text-ink font-mono text-[15px] px-3 py-2 focus:border-ink focus:outline-none"
              />
              <input
                type="text" inputMode="numeric" value={grantDays}
                onChange={(e) => setGrantDays(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="durata in giorni (0 = a vita)"
                className="w-full bg-black border border-ink-dim text-ink font-mono text-[15px] px-3 py-2 focus:border-ink focus:outline-none"
              />
              <p className="text-[11px] text-ink-dim">
                Prova a tempo: scegli i giorni (es. 14, 30). 0 o vuoto = a vita.
                Alla scadenza: 15 gg di grazia (sola lettura), poi il Premium decade.
              </p>
              <div className="flex gap-2">
                <Btn variant="primary" onClick={() => doGrantTo(true)} disabled={grantBusy || !grantTo.trim()}>
                  {grantBusy ? '…' : '⭐ Rendi Premium'}
                </Btn>
                <Btn variant="ghost" onClick={() => doGrantTo(false)} disabled={grantBusy || !grantTo.trim()}>
                  Togli
                </Btn>
              </div>
              {grantMsg && <p className="text-[12px] text-ink-dim break-words select-text">{grantMsg}</p>}
            </div>

            {/* Elenco degli utenti resi Premium */}
            <div className="space-y-2 border border-ink-dim/40 p-2">
              <div className="flex items-center justify-between">
                <p className="text-[13px] text-ink">📋 Utenti resi Premium</p>
                <button
                  onClick={loadPremiumList}
                  disabled={premiumListBusy}
                  className="text-[12px] text-ink-dim hover:text-ink disabled:opacity-40"
                >
                  {premiumListBusy ? '…' : (premiumList === null ? 'mostra' : '↻ aggiorna')}
                </button>
              </div>
              {premiumListErr && <p className="text-[12px] text-red-400 break-words select-text">{premiumListErr}</p>}
              {premiumList !== null && !premiumListBusy && premiumList.length === 0 && (
                <p className="text-[12px] text-ink-dim">nessun utente reso Premium finora.</p>
              )}
              {premiumList !== null && premiumList.length > 0 && (
                <>
                  <p className="text-[11px] text-ink-dim">{premiumList.length} utenti</p>
                  <ul className="space-y-1">
                    {premiumList.map((u) => (
                      <li key={u.sigil} className="flex items-center justify-between text-[13px] border-b border-ink-dim/20 pb-1">
                        <span className="text-ink font-mono truncate">@{u.sigil}</span>
                        <span className={`text-[11px] shrink-0 ml-2 ${
                          u.status === 'active' || u.status === 'lifetime' ? 'text-ink text-glow'
                            : u.status === 'grace' ? 'text-amber'
                              : 'text-red-400'}`}>
                          {u.status === 'lifetime' ? 'a vita'
                            : u.status === 'active' ? `attivo${u.daysLeft != null ? ` · ${u.daysLeft}g` : ''}`
                              : u.status === 'grace' ? `grazia${u.graceDaysLeft != null ? ` · ${u.graceDaysLeft}g` : ''}`
                                : 'scaduto'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <Btn variant="ghost" onClick={async () => {
              setModeMsg(null);
              if (!confirm('Migrare le chat legacy (backfill participantsSigils)? Operazione una tantum, sicura e idempotente.')) return;
              setModeBusy(true);
              try {
                const r = await callFn('migrateLegacyChats')({});
                const d = r?.data || {};
                setModeMsg(`migrazione: scansionate ${d.totalScanned}, migrate ${d.migrated}, saltate ${d.skipped}, errori ${d.errors}`);
              } catch (e) {
                const m = String(e?.message || '');
                setModeMsg(m.includes('FOUNDER_ONLY') ? 'solo il founder può migrare.' : `errore: ${m}`);
              } finally { setModeBusy(false); }
            }} disabled={modeBusy}>
              🛠 Migra chat legacy
            </Btn>
            {modeMsg && <p className="text-[12px] text-ink-dim break-words select-text">{modeMsg}</p>}
          </Section>

          <Footer>
            <Btn variant="ghost" onClick={() => navigate('/inbox')}>◀ Inbox</Btn>
          </Footer>
        </>
      )}
    </Frame>
  );
}
